/* Rebels theme — storefront scripts (no dependencies) */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const TV = window.themeVariables || { routes: {}, strings: {} };
  const routes = TV.routes;
  const strings = TV.strings;
  const debounce = (fn, wait = 250) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); }; };
  const storage = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* ignore */ } }
  };

  /* ---------------------------------------------------------------- money */
  function formatMoney(cents, format = TV.moneyFormat || '{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const delim = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [intPart, decPart] = fixed.split('.');
      return intPart.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (decPart ? decimal + decPart : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = delim(cents, 0); break;
      case 'amount_with_comma_separator': value = delim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delim(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delim(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = delim(cents, 2, ' ', ','); break;
      default: value = delim(cents, 2);
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, value);
  }

  /* ---------------------------------------------------------------- overlay & drawers */
  const overlayEl = () => $('[data-page-overlay]');
  let lastFocus = null;

  function openDrawer(drawer) {
    if (!drawer) return;
    closeAll(false);
    lastFocus = document.activeElement;
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlayEl() && overlayEl().classList.add('is-visible');
    document.body.classList.add('is-locked');
    const focusable = drawer.querySelector('button, [href], input, textarea');
    focusable && setTimeout(() => focusable.focus({ preventScroll: true }), 60);
  }

  function closeAll(restoreFocus = true) {
    $$('.drawer.is-open').forEach((d) => { d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); });
    closeQuickBuy(false);
    $$('[data-size-guide].is-open').forEach((g) => { g.classList.remove('is-open'); g.setAttribute('aria-hidden', 'true'); });
    overlayEl() && overlayEl().classList.remove('is-visible');
    document.body.classList.remove('is-locked');
    if (restoreFocus && lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  /* ---------------------------------------------------------------- header */
  const Header = {
    init() {
      this.section = $('.shopify-section--header');
      this.header = $('[data-header]');
      this.announcement = $('.shopify-section--announcement-bar');
      if (!this.header) return;
      this.sticky = this.header.dataset.sticky === 'true';
      this.hideOnScroll = this.header.dataset.hideOnScroll === 'true';
      document.body.classList.toggle('has-sticky-header', this.sticky);
      this.detectTransparent();
      this.measure();
      window.addEventListener('resize', debounce(() => this.measure(), 100));
      if ('ResizeObserver' in window) new ResizeObserver(() => this.measure()).observe(this.header);
      this.lastY = window.scrollY;
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.onScroll();
      // keep header solid while a mega menu is focused/hovered on touch devices
      $$('.header__menu-item.has-mega > .header__menu-link', this.header).forEach((link) => {
        link.addEventListener('touchstart', (e) => {
          const item = link.parentElement;
          if (!item.classList.contains('is-open')) { e.preventDefault(); $$('.header__menu-item.is-open').forEach((i) => i.classList.remove('is-open')); item.classList.add('is-open'); }
        }, { passive: false });
      });
    },
    detectTransparent() {
      document.body.classList.remove('has-transparent-header', 'transparent-light', 'transparent-dark');
      if (!this.header.classList.contains('header--allow-transparent')) return;
      const firstSection = $('#main > .shopify-section');
      const marker = firstSection && firstSection.querySelector('[data-transparent-header]');
      if (!marker) return;
      document.body.classList.add('has-transparent-header', `transparent-${marker.dataset.transparentHeader || 'light'}`);
    },
    measure() {
      const aH = this.announcement ? this.announcement.offsetHeight : 0;
      const hH = this.header.offsetHeight;
      const root = document.documentElement.style;
      root.setProperty('--announcement-offset', `${this.sticky ? aH : 0}px`);
      root.setProperty('--header-height', `${hH}px`);
      root.setProperty('--header-offset', `${this.sticky ? aH + hH : 0}px`);
      root.setProperty('--header-group-height', `${aH + hH + 2}px`);
    },
    onScroll() {
      const y = window.scrollY;
      this.header.classList.toggle('is-solid', y > 20);
      if (this.sticky && this.hideOnScroll) {
        const goingDown = y > this.lastY && y > 200;
        const busy = this.header.classList.contains('is-search-open') || document.body.classList.contains('is-locked');
        if (!busy && Math.abs(y - this.lastY) > 4) document.body.classList.toggle('header-group-hidden', goingDown);
        if (y < 100) document.body.classList.remove('header-group-hidden');
      }
      this.lastY = y;
    }
  };

  /* ---------------------------------------------------------------- search */
  const Search = {
    init() {
      this.header = $('[data-header]');
      this.panel = $('[data-header-search]');
      if (!this.panel) return;
      this.input = $('[data-predictive-input]', this.panel);
      this.results = $('[data-predictive-results]', this.panel);
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-toggle]')) { e.preventDefault(); this.toggle(); }
        else if (e.target.closest('[data-search-close]')) { this.close(); }
        else if (this.header.classList.contains('is-search-open') && !e.target.closest('[data-header]')) { this.close(); }
      });
      this.input && this.input.addEventListener('input', debounce(() => this.fetch(), 280));
    },
    toggle() { this.header.classList.contains('is-search-open') ? this.close() : this.open(); },
    open() { this.header.classList.add('is-search-open'); document.body.classList.remove('header-group-hidden'); document.body.classList.add('search-open'); overlayEl() && overlayEl().classList.add('is-visible'); setTimeout(() => this.input && this.input.focus(), 50); },
    close() { if (!this.header || !this.header.classList.contains('is-search-open')) return; this.header.classList.remove('is-search-open'); document.body.classList.remove('search-open'); if (!$('.drawer.is-open')) overlayEl() && overlayEl().classList.remove('is-visible'); },
    async fetch() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; return; }
      const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,query,collection&resources[limit]=4&resources[limit_scope]=each`;
      try {
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.outerHTML : '';
        CardSliders.init(this.results);
      } catch (e) { this.results.innerHTML = ''; }
    }
  };

  /* ---------------------------------------------------------------- announcement carousel */
  function initAnnouncement(root = document) {
    $$('[data-announcement-carousel]', root).forEach((el) => {
      const slides = $$('.announcement-bar__slide', el);
      if (slides.length < 2 || el.dataset.bound) return;
      el.dataset.bound = '1';
      let i = 0;
      setInterval(() => { slides[i].classList.remove('is-selected'); i = (i + 1) % slides.length; slides[i].classList.add('is-selected'); }, (parseInt(el.dataset.autoplay, 10) || 5) * 1000);
    });
  }

  /* ---------------------------------------------------------------- slideshow */
  function initSlideshows(root = document) {
    $$('[data-slideshow]', root).forEach((el) => {
      const slides = $$('[data-slide]', el);
      const dots = $$('[data-slide-dot]', el);
      if (slides.length < 2 || el.dataset.bound) return;
      el.dataset.bound = '1';
      let index = 0; let timer = null;
      const go = (n) => {
        slides[index].classList.remove('is-selected'); dots[index] && dots[index].classList.remove('is-selected');
        index = (n + slides.length) % slides.length;
        slides[index].classList.add('is-selected'); dots[index] && dots[index].classList.add('is-selected');
        slides.forEach((s) => $$('video', s).forEach((v) => (s === slides[index] ? v.play().catch(() => {}) : v.pause())));
      };
      const autoplay = parseInt(el.dataset.autoplay, 10) || 0;
      const restart = () => { clearInterval(timer); if (autoplay) timer = setInterval(() => go(index + 1), autoplay * 1000); };
      dots.forEach((d) => d.addEventListener('click', () => { go(parseInt(d.dataset.slideDot, 10)); restart(); }));
      let startX = null;
      el.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      el.addEventListener('pointerup', (e) => { if (startX === null) return; const dx = e.clientX - startX; if (Math.abs(dx) > 50) { go(dx < 0 ? index + 1 : index - 1); restart(); } startX = null; });
      el.addEventListener('shopify:block:select', (e) => { const i = slides.indexOf(e.target.closest('[data-slide]')); if (i > -1) { go(i); clearInterval(timer); } });
      restart();
    });
  }

  /* ---------------------------------------------------------------- horizontal drag helper */
  function enableDrag(track) {
    if (track.dataset.dragBound) return;
    track.dataset.dragBound = '1';
    let down = false; let startX = 0; let startScroll = 0; let moved = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      down = true; moved = false; startX = e.clientX; startScroll = track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 6) { moved = true; track.classList.add('is-dragging'); }
      if (moved) track.scrollLeft = startScroll - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      if (moved) {
        const w = track.clientWidth;
        const target = Math.round(track.scrollLeft / w) * w;
        track.classList.remove('is-dragging');
        track.scrollTo({ left: target, behavior: 'smooth' });
      }
    });
    track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  /* ---------------------------------------------------------------- product card sliders */
  const CardSliders = {
    modelView() { return storage.get('modelViewPref') === 'true'; },
    init(root = document) {
      $$('[data-card-slider]', root).forEach((slider) => {
        if (slider.dataset.bound) return;
        slider.dataset.bound = '1';
        const track = $('[data-card-track]', slider);
        const slides = $$('.card-slider__slide', track);
        const drag = $('[data-card-drag]', slider);
        const count = slides.length;
        if (drag) drag.style.setProperty('--drag-width', `${100 / Math.max(count, 1)}%`);
        const update = () => {
          const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          if (drag) drag.style.setProperty('--drag-x', `${i * 100}%`);
          slider.dataset.index = i;
        };
        track.addEventListener('scroll', debounce(update, 30), { passive: true });
        const go = (dir) => {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          let n = i + dir;
          if (n < 0) n = count - 1;
          if (n >= count) n = 0;
          track.scrollTo({ left: n * track.clientWidth, behavior: Math.abs(n - i) > 1 ? 'auto' : 'smooth' });
        };
        const prev = $('[data-card-prev]', slider); const next = $('[data-card-next]', slider);
        prev && prev.addEventListener('click', (e) => { e.preventDefault(); go(-1); });
        next && next.addEventListener('click', (e) => { e.preventDefault(); go(1); });
        enableDrag(track);
        if (this.modelView()) this.jump(slider, true);
        update();
      });
    },
    jump(slider, instant) {
      const track = $('[data-card-track]', slider);
      const slides = $$('.card-slider__slide', track);
      const model = Math.min(parseInt(slider.dataset.modelIndex, 10) || 0, slides.length - 1);
      const index = this.modelView() ? model : 0;
      requestAnimationFrame(() => track.scrollTo({ left: index * track.clientWidth, behavior: instant ? 'auto' : 'smooth' }));
    },
    applyModelView() {
      $$('[data-card-slider]').forEach((s, i) => setTimeout(() => this.jump(s, false), Math.min(i, 12) * 40));
    },
    bindToggle(root = document) {
      $$('[data-model-toggle]', root).forEach((cb) => {
        if (cb.dataset.bound) return;
        cb.dataset.bound = '1';
        cb.checked = this.modelView();
        document.body.dataset.viewMode = cb.checked ? 'model' : 'plain';
        cb.addEventListener('change', () => {
          storage.set('modelViewPref', cb.checked ? 'true' : 'false');
          document.body.dataset.viewMode = cb.checked ? 'model' : 'plain';
          this.applyModelView();
        });
      });
    },
    teaser() {
      const slider = $('[data-card-slider]');
      if (!slider || !document.body.dataset.cardTeaser) return;
      let stopped = false;
      const img = () => { const s = slider.querySelectorAll('.card-slider__img')[parseInt(slider.dataset.index, 10) || 0]; return s; };
      const stop = () => { stopped = true; const el = img(); if (el) { el.style.transform = ''; } };
      ['touchstart', 'mousedown', 'scroll', 'keydown'].forEach((ev) => document.addEventListener(ev, stop, { once: true, passive: true }));
      const run = () => {
        if (stopped) return;
        const el = img(); if (!el) return;
        el.style.transform = 'translateX(-45px)';
        setTimeout(() => { el.style.transform = ''; }, 1000);
      };
      setTimeout(() => { run(); const t = setInterval(() => (stopped ? clearInterval(t) : run()), 5000); }, 3000);
    }
  };

  /* ---------------------------------------------------------------- cart */
  const Cart = {
    get drawer() { return $('[data-cart-drawer]'); },
    async refresh(open = false) {
      try {
        const res = await fetch(`${routes.root}?sections=cart-drawer`);
        const data = await res.json();
        this.render(data['cart-drawer']);
        if (open) this.open();
      } catch (e) { if (open) window.location.href = routes.cart; }
    },
    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-inner]');
      const current = $('[data-cart-drawer-inner]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      const countEl = doc.querySelector('[data-cart-count]');
      const count = countEl ? parseInt(countEl.textContent, 10) || 0 : 0;
      $$('[data-cart-dot]').forEach((d) => d.classList.toggle('is-visible', count > 0));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },
    open() {
      if (TV.cartType === 'page' || document.body.classList.contains('template-cart')) { window.location.href = routes.cart; return; }
      openDrawer(this.drawer);
    },
    async add(formData) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Error');
      if (json.sections) this.render(json.sections['cart-drawer']);
      else await this.refresh();
      return json;
    },
    async change(line, quantity) {
      const lineEl = $(`[data-cart-drawer] [data-line="${line}"]`);
      lineEl && lineEl.classList.add('is-loading');
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
      });
      const json = await res.json();
      if (json.sections) this.render(json.sections['cart-drawer']);
      if (json.status) alert(json.description || json.message);
    },
    init() {
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle && TV.cartType === 'drawer' && !document.body.classList.contains('template-cart')) { e.preventDefault(); this.open(); return; }
        const change = e.target.closest('[data-cart-drawer] [data-line-change]');
        if (change) { e.preventDefault(); this.change(parseInt(change.dataset.lineChange, 10), Math.max(0, parseInt(change.dataset.quantity, 10))); return; }
        const pageChange = e.target.closest('[data-cart-page] [data-line-change]');
        if (pageChange) {
          e.preventDefault();
          fetch(`${routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line: parseInt(pageChange.dataset.lineChange, 10), quantity: Math.max(0, parseInt(pageChange.dataset.quantity, 10)) }) })
            .then(() => window.location.reload());
          return;
        }
        const noteToggle = e.target.closest('[data-note-toggle]');
        if (noteToggle) {
          const panel = $('[data-note-panel]');
          if (panel) { panel.hidden = !panel.hidden; noteToggle.setAttribute('aria-expanded', String(!panel.hidden)); if (!panel.hidden) $('textarea', panel).focus(); }
        }
      });
      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-drawer] [data-line-input]');
        if (input) this.change(parseInt(input.dataset.lineInput, 10), Math.max(0, parseInt(input.value, 10) || 0));
        const pageInput = e.target.closest('[data-cart-page] [data-line-input]');
        if (pageInput) {
          fetch(`${routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line: parseInt(pageInput.dataset.lineInput, 10), quantity: Math.max(0, parseInt(pageInput.value, 10) || 0) }) })
            .then(() => window.location.reload());
        }
      });
      document.addEventListener('input', debounce((e) => {
        if (!e.target.matches('[data-cart-note]')) return;
        fetch(`${routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
      }, 500));
    }
  };

  /* ---------------------------------------------------------------- product */
  class ProductSection {
    constructor(el) {
      this.el = el;
      this.picker = $('[data-variant-picker]', el);
      const json = this.picker && $('[data-variants-json]', this.picker);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.formId = this.picker ? this.picker.dataset.formId : null;
      this.form = this.formId ? document.getElementById(this.formId) : $('[data-product-form]', el);
      this.initGallery();
      this.initPicker();
      this.initForm();
      this.initSizeGuide();
      this.initSticky();
      this.initQuantity();
    }

    initGallery() {
      this.gallery = $('[data-product-gallery]', this.el);
      if (!this.gallery) return;
      this.track = $('[data-gallery-track]', this.gallery);
      const counter = $('[data-gallery-current]', this.gallery);
      const slides = $$('.product-gallery__slide', this.track);
      const current = () => Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
      const go = (n) => { const count = slides.length; if (n < 0) n = count - 1; if (n >= count) n = 0; this.track.scrollTo({ left: n * this.track.clientWidth, behavior: Math.abs(n - current()) > 1 ? 'auto' : 'smooth' }); };
      this.goTo = (n, instant) => this.track.scrollTo({ left: n * this.track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
      const prev = $('[data-gallery-prev]', this.gallery); const next = $('[data-gallery-next]', this.gallery);
      prev && prev.addEventListener('click', () => go(current() - 1));
      next && next.addEventListener('click', () => go(current() + 1));
      this.track.addEventListener('scroll', debounce(() => {
        const i = current();
        if (counter) counter.textContent = i + 1;
        slides.forEach((s, idx) => { const v = $('video', s); if (v) (idx === i ? v.play().catch(() => {}) : v.pause()); });
      }, 40), { passive: true });
      enableDrag(this.track);
      const selected = $('.product-gallery__slide.is-selected', this.track);
      if (selected) requestAnimationFrame(() => this.goTo(parseInt(selected.dataset.index, 10), true));
      const fs = $('[data-fullscreen-gallery]', this.gallery);
      if (fs) {
        const close = () => { fs.classList.remove('is-open'); fs.setAttribute('aria-hidden', 'true'); document.body.classList.remove('is-locked'); };
        $$('[data-gallery-open]', this.gallery).forEach((btn) => btn.addEventListener('click', () => {
          if (this.track.classList.contains('is-dragging')) return;
          if (fs.parentElement !== document.body) document.body.appendChild(fs);
          fs.classList.add('is-open'); fs.setAttribute('aria-hidden', 'false'); document.body.classList.add('is-locked');
          const target = $(`.fullscreen-gallery__slide[data-index="${btn.dataset.galleryOpen}"]`, fs);
          target && target.scrollIntoView({ behavior: 'instant', block: 'start' });
        }));
        fs.addEventListener('click', (e) => { if (!e.target.closest('img') || e.target.closest('[data-gallery-close]')) close(); else close(); });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && fs.classList.contains('is-open')) close(); });
      }
    }

    selectedOptions() {
      const opts = [];
      $$('fieldset[data-option-position]', this.picker).forEach((fs) => {
        const checked = $('input[data-option-input]:checked', fs);
        opts[parseInt(fs.dataset.optionPosition, 10) - 1] = checked ? checked.value : null;
      });
      return opts;
    }

    findVariant(opts) {
      return this.variants.find((v) => v.options.every((o, i) => opts[i] == null || o === opts[i]));
    }

    initPicker() {
      if (!this.picker || !this.variants.length) return;
      this.picker.addEventListener('change', (e) => { if (e.target.matches('[data-option-input]')) this.onVariantChange(); });
      this.updateAvailability();
    }

    updateAvailability() {
      const opts = this.selectedOptions();
      $$('fieldset[data-option-position]', this.picker).forEach((fs) => {
        const pos = parseInt(fs.dataset.optionPosition, 10) - 1;
        $$('input[data-option-input]', fs).forEach((input) => {
          const test = opts.slice(); test[pos] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => test[i] == null || o === test[i]));
          const label = input.nextElementSibling;
          label && label.classList.toggle('is-disabled', !match || !match.available);
        });
        const valueEl = $('[data-option-value]', fs);
        if (valueEl) valueEl.textContent = opts[pos] || '';
      });
    }

    onVariantChange() {
      const variant = this.findVariant(this.selectedOptions());
      this.updateAvailability();
      this.setButtons(variant);
      if (!variant) return;
      const idInput = this.form && $('[data-variant-id]', this.form);
      if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      const priceList = $('[data-price-list]', this.el);
      if (priceList) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceList.classList.toggle('is-on-sale', !!onSale);
        const sale = $('[data-sale-price]', priceList); const compare = $('[data-compare-price]', priceList);
        if (sale) sale.innerHTML = `<span class="sr-only">Sale price</span>${formatMoney(variant.price)}`;
        if (compare) { compare.hidden = !onSale; compare.innerHTML = onSale ? `<span class="sr-only">Regular price</span>${formatMoney(variant.compare_at_price)}` : ''; }
      }
      const inv = $('[data-inventory]', this.el);
      if (inv) inv.textContent = '';
      if (variant.featured_media && this.track) {
        const slide = $(`[data-media-id="${variant.featured_media.id}"]`, this.track);
        if (slide) this.goTo(parseInt(slide.dataset.index, 10));
      }
      const url = this.el.dataset.productUrl;
      if (url && !this.el.hasAttribute('data-quick-buy-product') && window.history.replaceState) {
        window.history.replaceState({}, '', `${url}?variant=${variant.id}`);
      }
    }

    setButtons(variant) {
      $$(`[data-add-to-cart]`, this.el).forEach((btn) => {
        const text = $('[data-add-text]', btn);
        btn.disabled = !variant || !variant.available;
        if (text) text.textContent = !variant ? strings.unavailable : variant.available ? strings.addToCart : strings.soldOut;
      });
    }

    initForm() {
      if (!this.form || this.form.dataset.bound) return;
      this.form.dataset.bound = '1';
      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const buttons = $$('[data-add-to-cart]', this.el);
        const error = $('[data-form-error]', this.el);
        if (error) error.hidden = true;
        buttons.forEach((b) => { b.classList.add('is-loading'); b.disabled = true; });
        try {
          await Cart.add(new FormData(this.form));
          if (this.el.hasAttribute('data-quick-buy-product')) closeQuickBuy(false);
          Cart.open();
        } catch (err) {
          if (error) { error.textContent = err.message; error.hidden = false; }
        } finally {
          buttons.forEach((b) => { b.classList.remove('is-loading'); });
          this.setButtons(this.findVariant(this.selectedOptions()) || { available: true });
        }
      });
    }

    initSizeGuide() {
      $$('[data-size-guide-open]', this.el).forEach((btn) => {
        const guide = document.getElementById(btn.dataset.sizeGuideOpen);
        if (!guide) return;
        btn.addEventListener('click', () => { guide.classList.add('is-open'); guide.setAttribute('aria-hidden', 'false'); });
        $$('[data-size-guide-close]', guide).forEach((c) => c.addEventListener('click', () => { guide.classList.remove('is-open'); guide.setAttribute('aria-hidden', 'true'); }));
        guide.addEventListener('click', (e) => { if (e.target === guide) { guide.classList.remove('is-open'); guide.setAttribute('aria-hidden', 'true'); } });
        const toggle = $('[data-unit-toggle]', guide);
        const table = $('[data-size-table]', guide);
        const apply = () => {
          if (!table) return;
          const inch = toggle ? toggle.checked : table.dataset.unit === 'in';
          $$('td[data-cm]', table).forEach((td) => {
            const raw = parseFloat(td.dataset.cm);
            td.textContent = isNaN(raw) ? td.dataset.cm : inch ? (Math.round((raw / 2.54) * 10) / 10).toFixed(1) : td.dataset.cm;
          });
        };
        toggle && toggle.addEventListener('change', apply);
        apply();
      });
    }

    initSticky() {
      const bar = $('[data-sticky-bar]', this.el);
      const target = this.form && $('[data-add-to-cart]', this.form);
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
      }).observe(target);
    }

    initQuantity() {
      this.el.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-qty-minus]'); const plus = e.target.closest('[data-qty-plus]');
        if (!minus && !plus) return;
        const input = $('input[name="quantity"]', this.el);
        if (!input) return;
        const v = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, v + (plus ? 1 : -1));
      });
    }
  }

  function initProducts(root = document) {
    $$('[data-product-section]', root).forEach((el) => { if (!el.dataset.bound) { el.dataset.bound = '1'; new ProductSection(el); } });
  }

  /* ---------------------------------------------------------------- quick buy */
  const quickBuyEl = () => $('#quick-buy');
  function closeQuickBuy(withOverlay = true) {
    const quickBuy = quickBuyEl();
    if (!quickBuy || !quickBuy.classList.contains('is-open')) return;
    quickBuy.classList.remove('is-open');
    quickBuy.setAttribute('aria-hidden', 'true');
    if (withOverlay) { overlayEl() && overlayEl().classList.remove('is-visible'); document.body.classList.remove('is-locked'); }
  }
  async function openQuickBuy(url) {
    const quickBuy = quickBuyEl();
    if (!quickBuy) { window.location.href = url; return; }
    const box = $('[data-quick-buy-content]', quickBuy);
    const sep = url.includes('?') ? '&' : '?';
    try {
      const html = await (await fetch(`${url}${sep}section_id=quick-buy`)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-quick-buy-product]');
      if (!content) { window.location.href = url; return; }
      box.innerHTML = '';
      box.appendChild(document.importNode(content, true));
      $$('.drawer.is-open').forEach((d) => { d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); });
      quickBuy.classList.add('is-open');
      quickBuy.setAttribute('aria-hidden', 'false');
      overlayEl() && overlayEl().classList.add('is-visible');
      document.body.classList.add('is-locked');
      initProducts(box);
    } catch (e) { window.location.href = url; }
  }

  /* ---------------------------------------------------------------- facets (AJAX filtering) */
  const Facets = {
    init() {
      document.addEventListener('change', (e) => {
        const form = e.target.closest('[data-facets-form]');
        if (!form) return;
        if (e.target.name === 'sort_by') $$('input[name="sort_by"]', form).forEach((i) => { if (i !== e.target) i.checked = false; });
        this.submit(form);
      });
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-facets-form]');
        if (!form) return;
        e.preventDefault();
        this.load(this.urlFor(form)).then(() => closeAll());
      });
      document.addEventListener('click', (e) => {
        const clear = e.target.closest('[data-facets-clear]');
        if (clear) { e.preventDefault(); this.load(clear.getAttribute('href')); }
      });
      window.addEventListener('popstate', () => { if ($('[data-collection-section]')) this.load(window.location.href, false); });
    },
    urlFor(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
      return `${form.getAttribute('action')}?${params.toString()}`;
    },
    submit: debounce((form) => { Facets.load(Facets.urlFor(form)); }, 350),
    async load(url, push = true) {
      const section = $('[data-collection-section]');
      if (!section) { window.location.href = url; return; }
      const results = $('[data-collection-results]', section);
      results && results.classList.add('is-loading');
      const sep = url.includes('?') ? '&' : '?';
      try {
        const html = await (await fetch(`${url}${sep}section_id=${section.dataset.sectionId}`)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newResults = doc.querySelector('[data-collection-results]');
        if (results && newResults) results.innerHTML = newResults.innerHTML;
        const counts = $$('[data-products-count]', doc);
        $$('[data-products-count]').forEach((c, i) => { if (counts[i]) c.textContent = counts[i].textContent; });
        const newBody = doc.querySelector('#facets-drawer .facets__body');
        const body = $('#facets-drawer .facets__body');
        if (newBody && body) body.innerHTML = newBody.innerHTML;
        const newFooter = doc.querySelector('#facets-drawer .facets__footer');
        const footer = $('#facets-drawer .facets__footer');
        if (newFooter && footer) footer.innerHTML = newFooter.innerHTML;
        if (push) window.history.pushState({}, '', url);
        CardSliders.init(results || document);
        reveal(results || document);
      } catch (e) {
        window.location.href = url;
      } finally {
        results && results.classList.remove('is-loading');
      }
    }
  };

  /* ---------------------------------------------------------------- tabs */
  function initTabs(root = document) {
    $$('[data-tabs]', root).forEach((wrap) => {
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = '1';
      $$('[data-tab-target]', wrap).forEach((btn) => btn.addEventListener('click', () => {
        const nav = btn.closest('[role="tablist"]');
        $$('[data-tab-target]', nav).forEach((b) => { b.classList.toggle('is-active', b === btn); b.setAttribute('aria-selected', String(b === btn)); });
        const panel = document.getElementById(btn.dataset.tabTarget);
        if (!panel) return;
        $$('.tab-panel', panel.parentElement).forEach((p) => p.classList.toggle('is-active', p === panel));
        CardSliders.init(panel);
      }));
    });
  }

  /* ---------------------------------------------------------------- recommendations */
  function initRecommendations(root = document) {
    $$('product-recommendations[data-url]', root).forEach(async (el) => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = '1';
      try {
        const html = await (await fetch(el.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
        reveal(el);
      } catch (e) { /* ignore */ }
    });
  }

  /* ---------------------------------------------------------------- reveal on scroll */
  let revealObserver = null;
  function reveal(root = document) {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-revealed'); revealObserver.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -40px 0px' });
    $$('product-card:not(.reveal)', root).forEach((card, i) => {
      card.classList.add('reveal');
      card.style.transitionDelay = `${(i % 4) * 60}ms`;
      revealObserver.observe(card);
    });
  }

  /* ---------------------------------------------------------------- global clicks */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(document.getElementById(opener.dataset.drawerOpen)); return; }
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-page-overlay]')) { closeAll(); return; }
    const qb = e.target.closest('[data-quick-buy]');
    if (qb) { e.preventDefault(); openQuickBuy(qb.dataset.quickBuy); return; }
    if (e.target.closest('[data-quick-buy-close]') || e.target === quickBuyEl()) { closeAll(); return; }
    const collapsible = e.target.closest('[data-collapsible-toggle]');
    if (collapsible) { collapsible.setAttribute('aria-expanded', String(collapsible.getAttribute('aria-expanded') !== 'true')); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAll(); Search.close && Search.close(); } });
  document.addEventListener('submit', (e) => {
    const confirmMsg = e.target.dataset && e.target.dataset.confirm;
    if (confirmMsg && !window.confirm(confirmMsg)) e.preventDefault();
  });

  /* ---------------------------------------------------------------- boot */
  function initAll(root = document) {
    initAnnouncement(root);
    initSlideshows(root);
    CardSliders.init(root);
    CardSliders.bindToggle(root);
    initProducts(root);
    initTabs(root);
    initRecommendations(root);
    reveal(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    Header.init();
    Search.init();
    Cart.init();
    Facets.init();
    initAll();
    CardSliders.teaser();
    // Move the cart drawer + sidebar out of stacking contexts
    $$('.drawer').forEach((d) => { if (d.parentElement !== document.body) document.body.appendChild(d); });
  });

  // Theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const id = e.target.id.replace('shopify-section-', '');
    $$(`body > .drawer`).forEach((d) => { if (e.target.querySelector(`#${d.id}`)) d.remove(); });
    if (e.target.classList.contains('shopify-section--header')) { Header.init(); Search.init(); }
    initAll(e.target);
    $$('.drawer', e.target).forEach((d) => document.body.appendChild(d));
    Header.detectTransparent && Header.header && Header.detectTransparent();
    return id;
  });
  document.addEventListener('shopify:section:unload', (e) => {
    $$('body > .drawer').forEach((d) => { if (d.dataset.sectionId === e.detail.sectionId) d.remove(); });
  });
  document.addEventListener('shopify:block:select', (e) => {
    const item = e.target.closest('.header__menu-item');
    if (item) item.classList.add('is-open');
  });

  window.RebelsTheme = { Cart, formatMoney, openDrawer, closeAll, openQuickBuy };
})();
